import {
  Alert,
  Button,
  Card,
  Col,
  ColorPicker,
  Form,
  Input,
  List,
  Modal,
  Popconfirm,
  Row,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
  Upload,
  Checkbox,
} from 'antd'
import {
  CloseOutlined,
  CopyOutlined,
  DownloadOutlined,
  Html5Outlined,
  KeyOutlined,
  LinkOutlined,
  LoadingOutlined,
  PlusOutlined,
  SettingOutlined,
} from '@ant-design/icons'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'
import {
  createRitaInstance,
  createRitaIdentityKey,
  deleteRitaInstance,
  getRitaInstance,
  getRitaIdentityKeys,
  revokeRitaIdentityKey,
  rotateRitaIdentityKey,
  updateRitaInstance,
} from '../../services/ritaInstances'

const ritaURL = import.meta.env.VITE_RITA
const bubbleScriptURL = import.meta.env.VITE_RITA_BUBBLE_SCRIPT
const ritaAPIURL =
  import.meta.env.VITE_RITA_API || 'https://rita-api.verbeux.com.br'

export function RitaIntegration({ icon, label, description, assistantID }) {
  const [loading, setLoading] = useState(true)
  const [data, setData] = useState()
  const [buttonLoading, setButtonLoading] = useState(false)
  const [configOpen, setConfigOpen] = useState(false)
  const [identityOpen, setIdentityOpen] = useState(false)
  const [identityKeys, setIdentityKeys] = useState([])
  const [identityLoading, setIdentityLoading] = useState(false)
  const [identityName, setIdentityName] = useState('Site principal')
  const [allowedOrigins, setAllowedOrigins] = useState('')
  const [revealedSecret, setRevealedSecret] = useState('')
  const [tooltipVisible, setTooltipVisible] = useState(false)

  const {
    token: { colorPrimary },
  } = theme.useToken()

  useEffect(() => {
    let cancelled = false
    queueMicrotask(() => {
      if (!cancelled) setLoading(true)
    })
    getRitaInstance({ assistantID })
      .then(({ data }) => {
        if (cancelled) return
        if (data.length === 0) {
          setData(undefined)
          return
        }

        setData(data[0])
      })
      .catch((error) => {
        if (cancelled) return
        console.log(error)
        toast.error('Erro ao buscar integração com site.')
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [assistantID])

  const install = useCallback(() => {
    setButtonLoading(true)
    createRitaInstance({
      assistantID: parseInt(assistantID),
      active: true,
      allowDocumentDisplay: true,
      allowTriggerDisplay: true,
    })
      .then(({ data }) => {
        setData(data)
      })
      .catch((error) => {
        console.log(error)
        toast.error('Erro ao instalar integração com site.')
      })
      .finally(() => {
        setButtonLoading(false)
      })
  }, [assistantID])

  const uninstall = useCallback(() => {
    setButtonLoading(true)
    deleteRitaInstance(data.id)
      .then(() => {
        setData(undefined)
      })
      .catch((error) => {
        console.log(error)
        toast.error('Erro ao remover integração com site.')
      })
      .finally(() => {
        setButtonLoading(false)
      })
  }, [data])

  const update = useCallback(async () => {
    setButtonLoading(true)
    updateRitaInstance(data.id, { ...data })
      .then(({ data: updatedData }) => {
        setData({ ...updatedData })
        setConfigOpen(false)
        toast.success('Atualizado com sucesso!')
      })
      .catch((error) => {
        console.log(error)
        toast.error('Erro ao atualizar.')
      })
      .finally(() => {
        setButtonLoading(false)
      })
  }, [data])

  const loadIdentityKeys = useCallback(
    async (instanceID = data?.id) => {
      if (!instanceID) return
      setIdentityLoading(true)
      try {
        const { data: keys } = await getRitaIdentityKeys(instanceID)
        setIdentityKeys(Array.isArray(keys) ? keys : [])
      } catch (error) {
        console.log(error)
        toast.error('Erro ao carregar chaves de identidade.')
      } finally {
        setIdentityLoading(false)
      }
    },
    [data?.id]
  )

  const openIdentity = useCallback(() => {
    setRevealedSecret('')
    setIdentityOpen(true)
    void loadIdentityKeys()
  }, [loadIdentityKeys])

  const createIdentity = useCallback(async () => {
    const origins = allowedOrigins
      .split(/[\n,]/)
      .map((value) => value.trim())
      .filter(Boolean)
    if (!identityName.trim()) {
      toast.error('Informe um nome para a chave.')
      return
    }
    setIdentityLoading(true)
    try {
      const { data: result } = await createRitaIdentityKey(data.id, {
        name: identityName.trim(),
        allowedOrigins: origins,
      })
      setRevealedSecret(result.secret)
      setIdentityKeys((current) => [result.key, ...current])
      toast.success('Chave criada. Copie o segredo agora.')
    } catch (error) {
      console.log(error)
      toast.error(
        'Não foi possível criar a chave. Verifique as origens informadas.'
      )
    } finally {
      setIdentityLoading(false)
    }
  }, [allowedOrigins, data?.id, identityName])

  const revokeIdentity = useCallback(
    async (keyID) => {
      setIdentityLoading(true)
      try {
        const { data: revoked } = await revokeRitaIdentityKey(data.id, keyID)
        setIdentityKeys((current) =>
          current.map((key) => (key.id === keyID ? revoked : key))
        )
        toast.success('Chave revogada.')
      } catch (error) {
        console.log(error)
        toast.error('Não foi possível revogar a chave.')
      } finally {
        setIdentityLoading(false)
      }
    },
    [data?.id]
  )

  const rotateIdentity = useCallback(
    async (keyID) => {
      setIdentityLoading(true)
      try {
        const { data: result } = await rotateRitaIdentityKey(data.id, keyID)
        setRevealedSecret(result.secret)
        await loadIdentityKeys(data.id)
        toast.success('Chave rotacionada. Atualize o segredo no seu backend.')
      } catch (error) {
        console.log(error)
        toast.error('Não foi possível rotacionar a chave.')
      } finally {
        setIdentityLoading(false)
      }
    },
    [data?.id, loadIdentityKeys]
  )

  const copyToClipboard = async (text, setTooltipOpen) => {
    await navigator.clipboard
      .writeText(text)
      .then(() => {
        setTooltipOpen(true)
        setTimeout(() => setTooltipOpen(false), 2000)
      })
      .catch((err) => console.error('Falha ao copiar: ', err))
  }

  const textToCopy = (id, url, color) => {
    return `<script src="${bubbleScriptURL}" data-rita-id="${id}" rita-url="${url}" ${color ? `bubble-color='${color}'` : ''}></script>`
  }

  const serverIdentityExample = data
    ? `POST ${ritaAPIURL}/v1/rita/${data.uuid}/identity-tokens
Authorization: RitaKey <CHAVE_SECRETA>
Content-Type: application/json

{
  "email": "cliente@exemplo.com",
  "external_user_id": "user_123",
  "external_conversation_id": "ticket_456",
  "name": "Cliente",
  "origin": "https://seu-site.com"
}`
    : ''

  const browserIdentityExample = `window.addEventListener('rita:widget-ready', async (event) => {
  // Seu backend troca a chave secreta por um token curto e seguro.
  const { accessToken } = await fetch('/api/rita/identity').then(r => r.json());
  event.detail.setIdentityToken(accessToken);
});`

  const uploadButton = (
    <Button type={'text'} style={{ border: 0, background: 'none' }}>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <Typography.Title level={5} style={{ margin: 0, padding: 0 }}>
        Logo
      </Typography.Title>
    </Button>
  )

  return (
    <>
      <Modal
        width={760}
        title="Identidade e histórico do Rita"
        open={identityOpen}
        footer={null}
        destroyOnHidden
        onCancel={() => {
          setIdentityOpen(false)
          setRevealedSecret('')
        }}
      >
        <Space
          direction="vertical"
          size="large"
          style={{ width: '100%', marginTop: 12 }}
        >
          <Alert
            type="info"
            showIcon
            message="Autenticação segura para recuperar conversas"
            description="Use a chave somente no seu backend. Ele gera um token temporário por usuário; o navegador recebe apenas esse token via postMessage."
          />

          <Card size="small" title="Criar chave de servidor">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Input
                value={identityName}
                maxLength={100}
                placeholder="Nome da chave"
                onChange={({ target }) => setIdentityName(target.value)}
              />
              <Input.TextArea
                value={allowedOrigins}
                rows={3}
                placeholder={'https://seu-site.com\nhttps://app.seu-site.com'}
                onChange={({ target }) => setAllowedOrigins(target.value)}
              />
              <Typography.Text type="secondary">
                Uma origem por linha. Deixe vazio apenas para integrações
                internas sem restrição de origem.
              </Typography.Text>
              <Button
                type="primary"
                icon={<KeyOutlined />}
                loading={identityLoading}
                onClick={createIdentity}
              >
                Gerar chave
              </Button>
            </Space>
          </Card>

          {revealedSecret && (
            <Alert
              type="warning"
              showIcon
              message="Copie agora — este segredo não será exibido novamente"
              description={
                <Space.Compact style={{ width: '100%', marginTop: 8 }}>
                  <Input.Password
                    value={revealedSecret}
                    readOnly
                    visibilityToggle
                  />
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() => copyToClipboard(revealedSecret, () => {})}
                  >
                    Copiar
                  </Button>
                </Space.Compact>
              }
            />
          )}

          <Card
            size="small"
            title="Chaves existentes"
            loading={identityLoading}
          >
            <List
              locale={{ emptyText: 'Nenhuma chave criada' }}
              dataSource={identityKeys}
              renderItem={(key) => {
                const active =
                  !key.revokedAt &&
                  (!key.expiresAt || new Date(key.expiresAt) > new Date())
                return (
                  <List.Item
                    actions={
                      active
                        ? [
                            <Button
                              key="rotate"
                              type="link"
                              onClick={() => rotateIdentity(key.id)}
                            >
                              Rotacionar
                            </Button>,
                            <Popconfirm
                              key="revoke"
                              title="Revogar esta chave?"
                              onConfirm={() => revokeIdentity(key.id)}
                            >
                              <Button type="link" danger>
                                Revogar
                              </Button>
                            </Popconfirm>,
                          ]
                        : undefined
                    }
                  >
                    <List.Item.Meta
                      title={
                        <Space>
                          {key.name}
                          <Tag color={active ? 'green' : 'default'}>
                            {active ? 'Ativa' : 'Revogada'}
                          </Tag>
                        </Space>
                      }
                      description={`Prefixo: rita_key_${key.prefix} · ${key.allowedOrigins?.length ? key.allowedOrigins.join(', ') : 'todas as origens'}`}
                    />
                  </List.Item>
                )
              }}
            />
          </Card>

          <Card
            size="small"
            title="1. Troque a chave por um token no seu backend"
          >
            <Typography.Paragraph copyable={{ text: serverIdentityExample }}>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {serverIdentityExample}
              </pre>
            </Typography.Paragraph>
          </Card>
          <Card
            size="small"
            title="2. Entregue somente o token temporário ao widget"
          >
            <Typography.Paragraph copyable={{ text: browserIdentityExample }}>
              <pre style={{ whiteSpace: 'pre-wrap', margin: 0 }}>
                {browserIdentityExample}
              </pre>
            </Typography.Paragraph>
          </Card>
        </Space>
      </Modal>
      <Modal
        title="Configurar Chat Externo"
        open={configOpen}
        cancelText={'Cancelar'}
        confirmLoading={buttonLoading}
        okText={'Salvar'}
        onOk={update}
        onCancel={() => setConfigOpen(false)}
      >
        <br />
        <Row gutter={[12, 12]}>
          <Col span={6}>
            <Upload
              name="avatar"
              listType="picture-circle"
              className="avatar-uploader"
              showUploadList={false}
              action="https://run.mocky.io/v3/435e224c-44fb-4773-9faf-380c5e6a2188"
              beforeUpload={() => false}
              onChange={(e) => {
                setData((old) => ({ ...old, img: e.file }))
              }}
            >
              {data?.avatar || data?.img ? (
                <img
                  src={
                    data?.img
                      ? window.URL.createObjectURL(data?.img)
                      : data?.avatar + '?last=' + data.updatedAt
                  }
                  loading={'eager'}
                  alt="avatar"
                  style={{
                    width: '100%',
                    borderRadius: '100%',
                  }}
                />
              ) : (
                uploadButton
              )}
            </Upload>
          </Col>
          <Col span={18}>
            <Form layout={'vertical'}>
              <Form.Item label={'Nome'}>
                <Input
                  value={data?.name}
                  maxLength={20}
                  onChange={({ target: { value } }) =>
                    setData((old) => ({ ...old, name: value }))
                  }
                />
              </Form.Item>

              <Form.Item label={'Mensagem Inicial'}>
                <Input.TextArea
                  value={data?.initialMessage}
                  placeholder="Olá, sou um assistente virtual e estou pronto para lhe auxiliar"
                  onChange={({ target: { value } }) =>
                    setData((old) => ({
                      ...old,
                      initialMessage: value,
                    }))
                  }
                />
              </Form.Item>

              <Form.Item label={'Cor'}>
                <ColorPicker
                  value={data?.primaryColor}
                  onChange={(color) =>
                    setData((old) => ({
                      ...old,
                      primaryColor: color.toHexString(),
                    }))
                  }
                  defaultValue={colorPrimary}
                  showText
                />
              </Form.Item>

              <Form.Item>
                <Checkbox
                  checked={data?.allowDocumentDisplay}
                  onChange={({ target: { checked } }) =>
                    setData((old) => ({
                      ...old,
                      allowDocumentDisplay: checked,
                    }))
                  }
                >
                  Mostrar Documentos
                </Checkbox>
              </Form.Item>

              <Form.Item>
                <Checkbox
                  checked={data?.allowTriggerDisplay}
                  onChange={({ target: { checked } }) =>
                    setData((old) => ({
                      ...old,
                      allowTriggerDisplay: checked,
                    }))
                  }
                >
                  Mostrar Triggers
                </Checkbox>
              </Form.Item>
            </Form>
          </Col>
        </Row>
      </Modal>
      <Card
        loading={loading}
        title={
          <Typography.Title level={5} ellipsis={{ tooltip: label }}>
            {icon} {label}
          </Typography.Title>
        }
        extra={
          <Button
            type={data ? 'default' : 'primary'}
            loading={loading || buttonLoading}
            danger={!!data}
            onClick={() => (data ? uninstall() : install())}
            icon={data ? <CloseOutlined /> : <DownloadOutlined />}
          >
            {loading ? undefined : data ? 'Remover' : 'Instalar'}
          </Button>
        }
        actions={
          data
            ? [
                <SettingOutlined onClick={() => setConfigOpen(true)} />,
                <Tooltip title="Copiado!" open={tooltipVisible}>
                  <Html5Outlined
                    onClick={() =>
                      copyToClipboard(
                        textToCopy(data.uuid, ritaURL, data.primaryColor),
                        setTooltipVisible
                      )
                    }
                  />
                </Tooltip>,
                <KeyOutlined key="identity" onClick={openIdentity} />,
                <a
                  href={`${ritaURL}/generative/${data.uuid}`}
                  target={'_blank'}
                  rel={'noopener noreferrer'}
                  aria-label={'Abrir chat externo da Rita'}
                >
                  <LinkOutlined />
                </a>,
              ]
            : undefined
        }
      >
        {description}
      </Card>
    </>
  )
}
